/**
 * "Add a comment" on the ticket detail page.
 *
 * Only rendered when `ADD_COMMENT` is in the ticket's `permitted_operations` —
 * the frontend never guesses what Ivanti will accept, and the backend re-checks
 * anyway (409 `TICKET_OPERATION_NOT_PERMITTED`).
 *
 * Files can go on a comment, because "can you send a screenshot?" arrives after
 * the ticket exists far more often than before it, and a portal with nowhere to
 * put the answer is a portal people reply to by email.
 *
 * The attach-a-file prompt is deliberately **not** enabled here, and that is a
 * product decision rather than an oversight: a comment is someone *volunteering*
 * an update, and interrogating a volunteer is how you stop getting updates.
 * "Any news?" and "still happening this morning" are worth having, and worth
 * having at zero friction. The prompt is reserved for the information request,
 * where IT is already blocked and explicitly waiting — see
 * `ticket-info-requests.tsx`. A prompt in front of every box is a prompt people
 * learn to dismiss on sight, which is precisely what would break it there.
 */

import { Alert } from "@norican/react";

import { useLanguage, useT } from "@/i18n";

import { useAddTicketComment } from "../api/queries";
import { useFeedbackToast } from "../lib/use-feedback-toast";
import { ComposeForm } from "./compose-form";

export interface TicketCommentFormProps {
  ticketId: string;
  /**
   * Offer files on the comment. Off for someone named on the request rather
   * than its Customer: they may comment, not attach (2 Oct), and the server
   * refuses a comment carrying files from them.
   */
  allowAttachments?: boolean;
  /**
   * On a finished ticket (9 Oct): "reopen" says the reply reopens it and IT
   * is told; "notify" says IT is told. Absent on an ordinary ticket.
   */
  finished?: "reopen" | "notify";
}

export function TicketCommentForm({
  ticketId,
  allowAttachments = true,
  finished,
}: TicketCommentFormProps) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  const toast = useFeedbackToast();
  const mutation = useAddTicketComment(ticketId);

  const handleSubmit = async (body: string, attachmentIds: string[]) => {
    // `body_lang` is required and is the language the user actually typed in —
    // the server stores the text verbatim under it and never translates on
    // write, which is what keeps the Ivanti journal an audit trail.
    const result = await mutation.mutateAsync({
      body,
      body_lang: language,
      attachment_ids: attachmentIds,
    });
    toast.success(
      finished === "reopen"
        ? t("comment.postedReopened")
        : finished === "notify"
          ? t("comment.postedNotified")
          : t("comment.posted"),
    );
    return result;
  };

  const form = (
    <ComposeForm
      name="comment"
      label={t("comment.label")}
      placeholder={t("comment.placeholder")}
      submitLabel={t("comment.send")}
      onSubmit={handleSubmit}
      attachments={
        allowAttachments
          ? { label: t("comment.attachLabel"), hint: t("comment.attachHint") }
          : undefined
      }
      pending={mutation.isPending}
      error={mutation.error}
    />
  );

  if (finished === undefined) {
    return form;
  }
  // Said before sending, not after (LIVE SR 10578: the requester expected a
  // reply to reopen the request, and it did not).
  return (
    <div className="flex flex-col gap-3">
      <Alert tone="info">
        {finished === "reopen" ? t("comment.reopensHint") : t("comment.notifiesHint")}
      </Alert>
      {form}
    </div>
  );
}
